import CouponIcon from "@assets/images/coupon-icon.svg";
import {
  Card,
  IconButton,
  makeStyles,
  TableBody,
  TableCell,
  TableFooter,
  TableRow,
  Tooltip,
  Typography
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import CardTitle from "@saleor/components/CardTitle";
import Checkbox from "@saleor/components/Checkbox";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TableHead from "@saleor/components/TableHead";
import TablePagination from "@saleor/components/TablePagination";
import { CouponCollectionAssignedCouponsList_couponCollection_coupons_edges_node } from "@saleor/coupons/types/CouponCollectionAssignedCouponsList";
import { renderCollection } from "@saleor/misc";
import { ListActions, ListProps } from "@saleor/types";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

const useStyles = makeStyles(
  theme => ({
    [theme.breakpoints.up("lg")]: {
      colActions: {
        width: 120
      },
      colEmail: {
        width: 300
      },
      colName: {
        width: "auto"
      }
    },
    colActions: {
      textAlign: "right"
    },
    noCouponText: {
      alignItems: "center",
      display: "flex",
      flexDirection: "column",
      fontSize: "17.5px",
      marginLeft: "10px"
    },
    tableTruncate: {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap"
    },
    ticketIconContainer: {
      alignItems: "center",
      display: "flex",
      flexDirection: "column",
      fontSize: "1rem",
      justifyContent: "space-between",
      padding: "1.5rem"
    },
    titleBar: {
      "& span": {
        flex: "none",
        fontWeight: "bolder",
        lineBreak: "anywhere",
        width: "-webkit-fill-available"
      }
    },
    deleteIcon: {
      "&:hover": {
        backgroundColor: "transparent",
        color: theme.palette.primary.main
      }
    }
  }),
  { name: "CouponCollectionAssignedCoupons" }
);

const numberOfColumns = 5;
const NoCouponAssignedComponent = () => {
  const classes = useStyles({});
  return (
    <div className={classes.ticketIconContainer}>
      <img src={CouponIcon} alt=""></img>
      <span className={classes.noCouponText}>
        <strong>No coupons assigned yet!</strong>
        <span>Coupons assigned will be displayed here.</span>
      </span>
    </div>
  );
};

interface CouponCollectionAssignedCouponsProps extends ListActions, ListProps {
  coupons?: CouponCollectionAssignedCouponsList_couponCollection_coupons_edges_node[];
  onUnassign?: (string) => void;
  listElements: string[];
}

const CouponCollectionAssignedCoupons: React.FC<CouponCollectionAssignedCouponsProps> = props => {
  const {
    coupons,
    toggle,
    isChecked,
    selected,
    toggleAll,
    settings,
    onUpdateListSettings,
    pageInfo,
    onNextPage,
    onPreviousPage,
    onUnassign,
    toolbar,
    listElements
  } = props;

  const intl = useIntl();
  const classes = useStyles(props);

  return (
    <Card>
      <CardTitle
        className={classes.titleBar}
        title={intl.formatMessage({
          defaultMessage: "Assigned Coupons",
          description: "header"
        })}
      />
      {coupons?.length === 0 ? (
        <NoCouponAssignedComponent />
      ) : (
        <ResponsiveTable>
          <TableHead
            colSpan={numberOfColumns}
            selected={selected}
            disabled={false}
            toolbar={toolbar}
            items={coupons}
            toggleAll={toggleAll}
          >
            <TableCellHeader>
              <FormattedMessage
                defaultMessage="Coupon Title"
                description="Coupon title"
              />
            </TableCellHeader>
            <TableCellHeader>
              <FormattedMessage
                defaultMessage="Classification"
                description="Coupon classification"
              />
            </TableCellHeader>
            <TableCellHeader>
              <FormattedMessage
                defaultMessage="Status"
                description="Coupon status"
              />
            </TableCellHeader>
            <TableCellHeader textAlign="right">
              <FormattedMessage
                defaultMessage="Action"
                description="Coupon delete icons"
              />
            </TableCellHeader>
          </TableHead>
          <TableFooter>
            <TableRow>
              <TablePagination
                colSpan={numberOfColumns}
                settings={settings}
                hasNextPage={pageInfo ? pageInfo.hasNextPage : false}
                onNextPage={onNextPage}
                onUpdateListSettings={onUpdateListSettings}
                hasPreviousPage={pageInfo ? pageInfo.hasPreviousPage : false}
                onPreviousPage={onPreviousPage}
              />
            </TableRow>
          </TableFooter>
          <TableBody>
            {renderCollection(coupons, (coupon, index) => {
              const isSelected = coupon ? isChecked(coupon.id) : false;

              return (
                <TableRow
                  key={coupon ? coupon?.id : "skeleton"}
                  hover={!!coupon}
                  selected={isSelected}
                >
                  <TableCell padding="checkbox" id={`checkboxRow${index}`}>
                    <Checkbox
                      checked={isSelected}
                      disableClickPropagation
                      onChange={() => toggle(coupon.id)}
                      disabled={false}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={coupon?.name}>
                      <div className={classes.tableTruncate}>
                        {coupon?.name.length > 10
                          ? `${coupon?.name.slice(0, 10)}...`
                          : coupon?.name}
                      </div>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Typography>
                      {coupon?.dealType === "DEALS"
                        ? "Mall Deals"
                        : "Mall Events"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>
                      <strong>{coupon?.status}</strong>
                    </Typography>
                  </TableCell>
                  <TableCell className={classes.colActions}>
                    <IconButton
                      className={classes.deleteIcon}
                      id={`UnassignCouponBtnRow${index}`}
                      onClick={() => onUnassign(coupon.id)}
                      disabled={listElements.length > 0}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </ResponsiveTable>
      )}
    </Card>
  );
};
CouponCollectionAssignedCoupons.displayName = "CouponCollectionAssignedCoupons";
export default CouponCollectionAssignedCoupons;
